import { makeAutoObservable, runInAction } from 'mobx';
import { token } from '@/infrastructure/container';
import { type IBrowserLocale } from '@/services/BrowserLocale';
import { type ILocalStorage } from '@/services/LocalStorage';
import { type ILogger } from '@/services/Logger';
import en from '@/i18n/en';
import { type LocaleCode, type Messages, SUPPORTED_LOCALES } from '@/i18n/messages';

const STORAGE_KEY = 'beforered:locale';

// Vite code-splits each of these into its own chunk; only the matched locale is fetched.
const LOADERS: Record<Exclude<LocaleCode, 'en'>, () => Promise<{ default: Messages }>> = {
  fr: () => import('@/i18n/fr'),
  es: () => import('@/i18n/es'),
  de: () => import('@/i18n/de'),
  zh: () => import('@/i18n/zh'),
  ja: () => import('@/i18n/ja'),
};

export class LocaleStore {
  private _current: LocaleCode = 'en';
  private _messages: Messages = en;
  private _loading = false;

  constructor(
    private browserLocale: IBrowserLocale,
    private storage: ILocalStorage,
    private logger: ILogger,
  ) {
    makeAutoObservable(this);
    const stored = this.storage.get(STORAGE_KEY) as LocaleCode | null;
    const initial =
      stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)
        ? stored
        : this.browserLocale.preferredFrom(SUPPORTED_LOCALES);
    void this.setLocale(initial);
  }

  get current(): LocaleCode {
    return this._current;
  }

  get messages(): Messages {
    return this._messages;
  }

  get loading(): boolean {
    return this._loading;
  }

  get available(): readonly LocaleCode[] {
    return SUPPORTED_LOCALES;
  }

  setLocale = async (locale: LocaleCode): Promise<void> => {
    if (locale === this._current && !this._loading) return;
    this.storage.set(STORAGE_KEY, locale);
    if (locale === 'en') {
      this._current = 'en';
      this._messages = en;
      return;
    }
    this._loading = true;
    try {
      const mod = await LOADERS[locale]();
      runInAction(() => {
        this._current = locale;
        this._messages = mod.default;
        this._loading = false;
      });
    } catch (cause) {
      this.logger.warn('Locale load failed; falling back to English', locale, cause);
      runInAction(() => {
        this._current = 'en';
        this._messages = en;
        this._loading = false;
      });
    }
  };
}

export const TOKEN_LocaleStore = token<LocaleStore>('LocaleStore');
